import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { AutomataTranslateService } from "@shared/services/automata-translate.service";
import { Subject } from "rxjs";

export interface Breadcrumb {
	label: string;
	url?: string;
	showBreadcrumbLink?: boolean;
	icon?: string;
}

@Injectable({
	providedIn: "root"
})
export class AutomataBreadcrumbService {
	private _breadcrumbs = new Subject<Breadcrumb[]>();
	public breadcrumbs$ = this._breadcrumbs.asObservable();

	constructor(
		private router: Router,
		private automataTranslateService: AutomataTranslateService
	) {}

	watch(): void {
		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(() => {
				const snapshotRoot = this.router.routerState.snapshot.root;
				const breadcrumbs = this._createBreadcrumbList(snapshotRoot);
				this._breadcrumbs.next(breadcrumbs);
			});
	}

	private _checkBreadcrumbStatus(route: ActivatedRouteSnapshot): {
		routeFirstChild: ActivatedRouteSnapshot | null;
		routeDataBreadcrumb: string | undefined;
	} {
		const routeFirstChild = route.firstChild;
		const routeDataBreadcrumb = route.data.breadcrumb
			? String(route.data.breadcrumb)
			: undefined;

		return { routeFirstChild, routeDataBreadcrumb };
	}

	private _createBreadcrumb(
		route: ActivatedRouteSnapshot,
		routeUrl: string[]
	): Breadcrumb {
		return {
			label: String(route.data.breadcrumb) ?? "",
			url: "#/" + routeUrl.join("/"),
			icon: String(route.data.breadcrumbIcon) ?? "",
			showBreadcrumbLink: Boolean(route.data.showBreadcrumbLink)
		};
	}

	private _createBreadcrumbList(
		route: ActivatedRouteSnapshot,
		parentUrl: string[] = [],
		breadcrumbs: Breadcrumb[] = []
	): Breadcrumb[] {
		const { routeDataBreadcrumb, routeFirstChild } =
			this._checkBreadcrumbStatus(route);
		const routeUrl = [...parentUrl, ...route.url.map((url) => url.path)];

		if (routeDataBreadcrumb) {
			const breadcrumbLabel = this._getTranslatedBreadcrumbLabel(
				String(route.data.breadcrumb)
			);

			this._addBreadcrumbIfNotExists(
				breadcrumbLabel,
				breadcrumbs,
				route,
				routeUrl
			);
		}

		if (routeFirstChild) {
			this._createBreadcrumbList(routeFirstChild, routeUrl, breadcrumbs);
		}

		return breadcrumbs;
	}

	private _checkAllUpperCase(text?: string) {
		return text && text === text.toUpperCase();
	}

	private _getTranslatedBreadcrumbLabel(label?: string): string {
		if (!label) {
			return "";
		}

		return this._checkAllUpperCase(label)
			? this.automataTranslateService.getTranslationSync(label)
			: label;
	}

	private _addBreadcrumbIfNotExists(
		breadcrumbLabel: string,
		breadcrumbs: Breadcrumb[],
		route: ActivatedRouteSnapshot,
		routeUrl: string[]
	): void {
		if (
			!breadcrumbs.some(
				(breadcrumb) => breadcrumb.label === breadcrumbLabel
			)
		) {
			breadcrumbs.push({
				...this._createBreadcrumb(route, routeUrl),
				label: breadcrumbLabel
			});
		}
	}
}
